import React, { useState, useMemo } from 'react';

export default function CategorySidebar({
  categories,
  onSelectClass = () => {},
  onManage = () => {},
  onAddCategory = () => {}
}) {
  const [openSubjects, setOpenSubjects] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleSubject = (section, subject) => {
    const key = `${section}:${subject}`;
    setOpenSubjects(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return categories;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(categories).forEach(([sectionName, subjects]) => {
      const filteredSubjects = {};
      let hasMatchingContent = false;

      Object.entries(subjects).forEach(([subject, classes]) => {
        // Check if subject name matches
        const subjectMatches = subject.toLowerCase().includes(searchLower);
        
        // Filter classes that match the search term
        const matchingClasses = Array.isArray(classes) ? classes.filter(cls => {
          const displayName = typeof cls === 'string' ? cls : cls.name;
          return displayName.toLowerCase().includes(searchLower);
        }) : [];

        // Include subject if it matches or has matching classes
        if (subjectMatches || matchingClasses.length > 0) {
          filteredSubjects[subject] = subjectMatches ? classes : matchingClasses;
          hasMatchingContent = true;
        }
      });

      // Include section if it has matching content or section name matches
      if (hasMatchingContent || sectionName.toLowerCase().includes(searchLower)) {
        filtered[sectionName] = filteredSubjects;
      }
    });

    return filtered;
  }, [categories, searchTerm]);

  // Auto-expand subjects when searching
  React.useEffect(() => {
    if (searchTerm.trim()) {
      const newOpenSubjects = {};
      Object.entries(filteredCategories).forEach(([sectionName, subjects]) => {
        Object.keys(subjects).forEach(subject => {
          const key = `${sectionName}:${subject}`;
          newOpenSubjects[key] = true;
        });
      });
      setOpenSubjects(newOpenSubjects);
    }
  }, [searchTerm, filteredCategories]);

  // Helper function to highlight search matches
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-yellow-800 px-0.5 rounded">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="bg-white rounded-xl w-72 flex flex-col h-full max-h-[100%]">
      {/* Fixed Header Section */}
      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        {/* Header with Manage Category button */}
        <div className="relative w-full mb-4">
          <h3 className="text-lg font-bold text-gray-800">Categories</h3>
          <button
            onClick={onManage}
            className="absolute top-0 right-0 flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-1.5 py-2 rounded"
          >
            <span>Manage Categories</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 5v14m-7-7h14"
              />
            </svg>
          </button>
        </div>

        {/* Add New Category Button 
        <button
          onClick={onAddCategory}
          className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors mb-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 5v14m-7-7h14"
            />
          </svg>
          <span>Add New Category...</span>
        </button>
*/}
        {/* Search Input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0112.15 12.15z" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search categories, subjects, classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Sections */}
        {Object.keys(filteredCategories).length === 0 ? (
          <div className="text-center py-8">
            {searchTerm ? (
              <div>
                <div className="text-gray-400 text-sm mb-2">No results found</div>
                <div className="text-gray-400 text-xs">
                  Try adjusting your search terms or clear the search to see all categories
                </div>
              </div>
            ) : (
              <div>
                <div className="text-gray-400 text-sm mb-2">No classes found for this school</div>
                <div className="text-gray-400 text-xs">
                  Create categories, subjects, and classes using the "Manage Category" button above
                </div>
              </div>
            )}
          </div>
        ) : (
          Object.entries(filteredCategories).map(([sectionName, subjects]) => (
            <div key={sectionName}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {highlightMatch(sectionName, searchTerm)}
              </h4>

              <div className="space-y-2 pl-2">
                {Object.entries(subjects).map(([subject, classes]) => {
                  const key = `${sectionName}:${subject}`;
                  return (
                    <div key={key}>
                      <button
                        onClick={() => toggleSubject(sectionName, subject)}
                        className="flex items-center justify-between w-full text-left text-gray-800 hover:text-blue-600 font-medium"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rotate-45 bg-blue-500" />
                          <span>{highlightMatch(subject, searchTerm)}</span>
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`w-4 h-4 transform transition-transform duration-200 ${openSubjects[key] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {openSubjects[key] && (
                        <ul className="ml-6 mt-1 text-sm text-gray-600 space-y-1">
                          {Array.isArray(classes) && classes.length > 0 ? classes.map((cls, idx) => {
                            // Handle both legacy string format and new database object format
                            const displayName = typeof cls === 'string' ? cls : cls.name;
                            const classroomObject = typeof cls === 'string' ? cls : cls;
                            
                            return (
                              <li
                                key={typeof cls === 'string' ? idx : cls.id}
                                onClick={() => {
                                  console.log('CategorySidebar: Class clicked:', classroomObject);
                                  onSelectClass(classroomObject);
                                }}
                                className="cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                title={typeof cls === 'object' ? `Database ID: ${cls.id}` : 'Legacy hardcoded class'}
                              >
                                {highlightMatch(displayName, searchTerm)}
                                {typeof cls === 'object' && (
                                  <span className="ml-2 text-xs text-gray-400">#{cls.id}</span>
                                )}
                              </li>
                            );
                          }) : null}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}